import type { PicklistOption } from '@/hooks/usePsa.ts';
import { Label } from '@/components/ui/label.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx';

/** Radix Select can't hold an empty value, so "none" needs a name of its own. */
const NONE = '__none';

function toId(value: string): number | null {
  return value === NONE ? null : Number(value);
}

/** One of the PSA's lists as a select, storing the PSA's ID and showing its label. */
export function PicklistSelect({
  id,
  label,
  help,
  choices,
  value,
  onChange,
  noneLabel,
}: {
  id: string;
  label: string;
  help?: string;
  choices: PicklistOption[];
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  /** Offered when the setting may be left to the PSA; absent when it is required. */
  noneLabel?: string;
}) {
  // An inactive option stays listed only while it is the stored choice, so a saved
  // setting never silently disappears from the form.
  const shown = choices.filter((c) => c.active || String(value) === c.value);
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={value == null ? (noneLabel ? NONE : '') : String(value)}
        onValueChange={(v) => { onChange(toId(v)); }}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder="Choose…" />
        </SelectTrigger>
        <SelectContent>
          {noneLabel ? <SelectItem value={NONE}>{noneLabel}</SelectItem> : null}
          {shown.map((c) => (
            <SelectItem key={c.value} value={c.value}>
              {c.label}
              {c.active ? '' : ' (inactive)'}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {help ? <p className="text-xs text-muted-foreground">{help}</p> : null}
    </div>
  );
}
